import { EventEmitter, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams, HttpResponse, HttpXhrBackend } from '@angular/common/http';
import { Observable, of, ReplaySubject, Subject, Subscription, throwError as observableThrowError } from 'rxjs';
import { catchError, finalize, map, take, tap } from 'rxjs/operators';
import { ErrorCode } from '../../constants/error-codes';
import {
  CASAuthRequestProxy,
  CASAuthRequestProxyData,
} from '@app/origami-api/services/base/request-proxy/cas-auth.request-proxy.class';
import { cacheService } from '@app/origami-api/services/base/cache.service';
import { AdditionalRequestOptions } from '@app/origami-api/services/base/request-proxy/request-proxy.class';
import { BuildProps } from '@app/origami-api/build-props';

export interface OrigamiResponse<T> {
  AuthValidationUrl?: string;
  Success?: boolean;
  ErrorCode: number;
  Result: T;
  ErrorMessage?: string;
}

export type OrigamiHttpResponse = OrigamiResponse<any>;

export interface OnRequestResult {
  error: number | string | null;
  response: HttpResponse<OrigamiHttpResponse>;
}

export type OrigamiRequestRestParams = [any?, ErrorCode[]?, boolean?, AdditionalRequestOptions?];

/** Error codes that should be silently handled during validation requests */
const VALIDATION_SILENT_ERROR_CODES = [
  ErrorCode.DOCUMENT_TRANSIENT_UPLOAD_FAILED,
  ErrorCode.USER_VALIDATION_REQUIRED,
  ErrorCode.VALIDATED_USER_DIFFERS_FROM_CURRENT,
];

@Injectable()
export abstract class ServiceBase extends HttpClient {
  private static _lastRequestTime: number;
  private static _onRequest = new EventEmitter<OnRequestResult>();

  static get onRequest(): EventEmitter<OnRequestResult> {
    return this._onRequest;
  }

  static get lastRequestTime(): number {
    return this._lastRequestTime || 0;
  }

  get proxy() {
    return this.requestProxy;
  }

  abstract serviceUrl: string;

  constructor(
    backend: HttpXhrBackend,
    private requestProxy: CASAuthRequestProxy,
  ) {
    super(backend);
  }

  dropCacheSection() {
    cacheService.dropCacheSection(this.serviceUrl);
  }

  dropAllCache() {
    cacheService.dropAllCache();
  }

  protected basePost(
    url: string,
    body: any,
    options?: any,
    silentErrorCodes?: ErrorCode[],
    forceRequest?: boolean,
    additionalRequestOptions?: AdditionalRequestOptions,
  ): Observable<OrigamiHttpResponse> {
    const useCache = cacheService.isEnabled && !forceRequest;

    // Try to return cached response or join existing in-flight request
    if (useCache) {
      const cachedResponse = this.tryGetCachedResponse(url, body);
      if (cachedResponse) {
        return cachedResponse;
      }
      // Cache miss - create new in-flight request with subscriber isolation
      return this.createInFlightRequest(url, body, options, silentErrorCodes, additionalRequestOptions);
    }

    // Non-cached request - execute directly
    const requestFn = this.createRequestFunction(url, body, options, silentErrorCodes);

    return this.requestProxy.run(requestFn, additionalRequestOptions).pipe(
      tap(() => {
        ServiceBase._lastRequestTime = Date.now();
      }),
      catchError((error) => this.handleHttpError(error)),
    );
  }

  /**
   * Creates a new in-flight request with proper subscriber isolation.
   * The HTTP request is subscribed internally, so individual subscriber
   * cancellations won't cancel the underlying request unless all consumers leave.
   */
  private createInFlightRequest(
    url: string,
    body: any,
    options: any,
    silentErrorCodes?: ErrorCode[],
    additionalRequestOptions?: AdditionalRequestOptions,
  ): Observable<OrigamiHttpResponse> {
    // Use ReplaySubject to buffer the response for late subscribers
    const inFlightSubject = new ReplaySubject<OrigamiHttpResponse>(1);
    cacheService.put(this.serviceUrl, url, body, inFlightSubject);

    // Subscribe to HTTP request internally - this keeps the request alive
    // independent of individual subscriber cancellations
    const requestFn = this.createRequestFunction(url, body, options, silentErrorCodes);
    let httpSubscription: Subscription;
    let isCompleted = false;

    httpSubscription = this.requestProxy.run(requestFn, additionalRequestOptions).subscribe({
      next: (response) => {
        isCompleted = true;
        ServiceBase._lastRequestTime = Date.now();
        inFlightSubject.next(response);
        inFlightSubject.complete();
        // Only cache successful responses
        if (response.Success) {
          cacheService.put(this.serviceUrl, url, body, response);
        } else {
          cacheService.cleanupInFlight(this.serviceUrl, url, body);
        }
      },
      error: (error) => {
        isCompleted = true;
        // removeInFlight will call subject.error() to notify waiting subscribers
        cacheService.removeInFlight(this.serviceUrl, url, body, error);
      },
    });

    // First caller subscribes to the Subject (consumer count already set to 1 by cacheService.put)
    return inFlightSubject.asObservable().pipe(
      take(1),
      finalize(() => {
        const remainingConsumers = cacheService.removeConsumer(this.serviceUrl, url, body);

        // Cancel HTTP request if no consumers are waiting and request hasn't completed
        if (remainingConsumers === 0 && !isCompleted) {
          httpSubscription.unsubscribe();
          cacheService.removeInFlight(this.serviceUrl, url, body);
        }
      }),
    );
  }

  protected origamiPost(
    url: string,
    body: any,
    options?: any,
    silentErrorCodes?: ErrorCode[],
    forceRequest?: boolean,
    additionalRequestOptions?: AdditionalRequestOptions,
  ): Observable<OrigamiHttpResponse> {
    const fullUrl = BuildProps.origin + this.serviceUrl + url;
    return this.basePost(fullUrl, body, options, silentErrorCodes, forceRequest, additionalRequestOptions);
  }

  protected origamiGet(url: string): Observable<OrigamiHttpResponse> {
    const fullUrl = BuildProps.origin + this.serviceUrl + url;
    return super
      .get<OrigamiHttpResponse>(fullUrl, this.getOptions())
      .pipe(map((response: HttpResponse<OrigamiHttpResponse>) => this.mapApiResult(response, null)));
  }

  protected outerGet(url: string, options?): Observable<any> {
    const opts = this.getOptions(options);
    opts.withCredentials = false;
    return super.get<any>(url, opts);
  }

  // ============================================================================
  // Cache Helpers
  // ============================================================================

  /**
   * Attempts to return a cached response or join an existing in-flight request.
   * @returns Observable if cache hit, null if cache miss
   */
  private tryGetCachedResponse(url: string, body: any): Observable<OrigamiHttpResponse> | null {
    const cached = cacheService.get(this.serviceUrl, url, body);

    // Join existing in-flight request
    if (cached instanceof Subject) {
      cacheService.addConsumer(this.serviceUrl, url, body);
      return cached.asObservable().pipe(
        take(1),
        finalize(() => cacheService.removeConsumer(this.serviceUrl, url, body)),
      );
    }

    // Return cached response
    if (cached) {
      return of(cached);
    }

    return null;
  }

  // ============================================================================
  // Request Building
  // ============================================================================

  /**
   * Creates the request function that will be executed by the proxy.
   */
  private createRequestFunction(
    url: string,
    body: any,
    options: any,
    silentErrorCodes?: ErrorCode[],
  ): (
    params?: CASAuthRequestProxyData,
    additionalOptions?: AdditionalRequestOptions,
  ) => Observable<OrigamiHttpResponse> {
    return (params?: CASAuthRequestProxyData, additionalOptions?: AdditionalRequestOptions) => {
      const effectiveSilentCodes = this.getEffectiveSilentErrorCodes(silentErrorCodes, additionalOptions);
      const requestBody = this.buildRequestBody(body, params);

      return super
        .post<OrigamiHttpResponse>(url, requestBody, this.getOptions(options))
        .pipe(map((response: HttpResponse<OrigamiHttpResponse>) => this.mapApiResult(response, effectiveSilentCodes)));
    };
  }

  /**
   * Merges validation silent error codes if additional options are present.
   */
  private getEffectiveSilentErrorCodes(
    silentErrorCodes: ErrorCode[],
    additionalOptions?: AdditionalRequestOptions,
  ): ErrorCode[] {
    if (!additionalOptions) {
      return silentErrorCodes ?? [];
    }

    const codes = silentErrorCodes ? [...silentErrorCodes] : [];
    for (const code of VALIDATION_SILENT_ERROR_CODES) {
      if (!codes.includes(code)) {
        codes.push(code);
      }
    }
    return codes;
  }

  /**
   * Builds the final request body by merging validation params.
   */
  private buildRequestBody(body: any, params?: CASAuthRequestProxyData): any {
    if (!params?.validationData) {
      return body;
    }

    const validationData = this.extractValidationData(params.validationData);
    return Object.assign(body || {}, { validationData });
  }

  /**
   * Extracts only the defined validation fields from params.
   */
  private extractValidationData(source: CASAuthRequestProxyData['validationData']): Record<string, any> {
    const result: Record<string, any> = {};

    if (source.TransactionId) {
      result.TransactionId = source.TransactionId;
    }
    if (source.UserName) {
      result.UserName = source.UserName;
    }
    if (source.UserPassword) {
      result.UserPassword = source.UserPassword;
    }
    if (source.ValidationType !== null && source.ValidationType !== undefined) {
      result.ValidationType = source.ValidationType;
    }

    return result;
  }

  // ============================================================================
  // HTTP Helpers
  // ============================================================================

  private getOptions(options?: any): any {
    const opts = options || {};
    opts.responseType = 'json';
    opts.observe = 'response';
    opts.withCredentials = true;
    opts.headers = opts.headers || new HttpHeaders({ 'Content-Type': 'application/json' });
    opts.params = opts.httpParams && this.buildParams(opts.httpParams);
    return opts;
  }

  private buildParams(params: Record<string, any>): HttpParams {
    let httpParams = new HttpParams();
    Object.keys(params).forEach((key) => {
      httpParams = httpParams.set(key, params[key]);
    });
    return httpParams;
  }

  private mapApiResult(
    response: HttpResponse<OrigamiHttpResponse>,
    silentErrorCodes: ErrorCode[] = [],
  ): OrigamiHttpResponse {
    const location = response.headers.get('Location');
    if (location) {
      return this.createResult(location, response, null);
    }

    const origamiResponse: OrigamiHttpResponse = response.body;
    const errorCode = origamiResponse.ErrorCode;
    origamiResponse.Success = errorCode === ErrorCode.SUCCESS;

    const isSilent = silentErrorCodes?.includes(errorCode);
    return this.createResult(isSilent ? null : errorCode, response, origamiResponse);
  }

  private createResult(
    error: number | string | null,
    response: HttpResponse<OrigamiHttpResponse>,
    origamiResult: OrigamiHttpResponse,
  ): OrigamiHttpResponse {
    ServiceBase.onRequest.emit({ error, response });

    if (error !== null && (!origamiResult || !origamiResult.Success)) {
      throw origamiResult;
    }
    return origamiResult;
  }

  private handleHttpError(res: HttpResponse<any>): Observable<never> {
    ServiceBase.onRequest.emit({
      error: -res?.status,
      response: res,
    });
    return observableThrowError(() => res);
  }
}
